import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";

const preconnectOnce = (url: string) => {
  try {
    const u = new URL(url);
    const origin = `${u.protocol}//${u.host}`;
    if (document.querySelector(`link[data-preconnect="${origin}"]`)) return;
    const l = document.createElement("link");
    l.rel = "preconnect";
    l.href = origin;
    l.crossOrigin = "anonymous";
    l.setAttribute("data-preconnect", origin);
    document.head.appendChild(l);
  } catch {}
};

const bufferedAhead = (el: HTMLVideoElement) => {
  const t = el.currentTime;
  const r = el.buffered;
  for (let i = 0; i < r.length; i++) {
    if (t >= r.start(i) && t <= r.end(i)) return r.end(i) - t;
  }
  return 0;
};

export function useHlsPlayer(opts: {
  src?: string;
  active: boolean;
  muted: boolean;
  shouldPlay: boolean;
}) {
  const { src, active, muted, shouldPlay } = opts;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);

  const [ready, setReady] = useState(false);
  const [hasFrame, setHasFrame] = useState(false);

  const readyRef = useRef(false);

  const playSafely = async () => {
    try {
      await videoRef.current?.play();
    } catch {}
  };

  const destroy = () => {
    const el = videoRef.current;
    try {
      hlsRef.current?.destroy();
    } catch {}
    hlsRef.current = null;
    if (el) {
      try { el.pause(); } catch {}
      try { el.removeAttribute("src"); } catch {}
      try { el.load(); } catch {}
    }
    readyRef.current = false;
    setReady(false);
    setHasFrame(false);
  };

  // keep muted in sync
  useEffect(() => {
    if (videoRef.current) videoRef.current.muted = muted;
  }, [muted]);

  // attach HLS or native playback
  useEffect(() => {
    const el = videoRef.current;
    if (!active || !el || !src) {
      destroy();
      return;
    }

    destroy();
    el.playsInline = true as any;
    el.setAttribute("webkit-playsinline", "true");
    el.preload = "auto";
    el.muted = muted;

    const onFirstFrame = () => {
      setHasFrame(true);
    };
    el.addEventListener("loadeddata", onFirstFrame, { once: true });

    if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hlsRef.current = hls;

      preconnectOnce(src);

      let unlockCapTimer: ReturnType<typeof setTimeout> | null = null;
      const clampInitialABR = () => {
        const cap = Math.min(1, hls.levels.length - 1);
        hls.autoLevelCapping = cap;
        unlockCapTimer = setTimeout(() => {
          hls.autoLevelCapping = -1;
        }, 8000);
      };

      const tryAutoplayWhenBuffered = () => {
        if (readyRef.current) return;
        const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
        const needed = isMobile ? 3.0 : 2.5;
        if (bufferedAhead(el) >= needed) {
          readyRef.current = true;
          setReady(true);
          if (shouldPlay) void playSafely();
        }
      };

      hls.on(Hls.Events.MEDIA_ATTACHED, () => hls.loadSource(src));
      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        clampInitialABR();
        tryAutoplayWhenBuffered();
      });
      hls.on(Hls.Events.FRAG_BUFFERED, tryAutoplayWhenBuffered);

      hls.on(Hls.Events.ERROR, (_evt, data) => {
        if (!data?.fatal) return;
        if (data.type === Hls.ErrorTypes.NETWORK_ERROR) {
          try { hls.startLoad(); } catch {}
        } else if (data.type === Hls.ErrorTypes.MEDIA_ERROR) {
          try { hls.recoverMediaError(); } catch {}
        } else {
          destroy();
        }
      });

      hls.attachMedia(el);

      return () => {
        if (unlockCapTimer) clearTimeout(unlockCapTimer);
        destroy();
        el.removeEventListener("loadeddata", onFirstFrame);
      };
    }

    // native HLS (Safari/iOS)
    if (el.canPlayType("application/vnd.apple.mpegurl")) {
      el.src = src;
      const onReady = () => {
        const i = setInterval(() => {
          const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
          const needed = isMobile ? 3.0 : 2.5;
          if (bufferedAhead(el) >= needed) {
            clearInterval(i);
            readyRef.current = true;
            setReady(true);
            if (shouldPlay) void playSafely();
          }
        }, 100);
      };
      el.addEventListener("canplay", onReady, { once: true });

      return () => {
        el.removeEventListener("canplay", onReady);
        el.removeEventListener("loadeddata", onFirstFrame);
        destroy();
      };
    }

    return () => {
      el.removeEventListener("loadeddata", onFirstFrame);
      destroy();
    };
  }, [src, active]);

  // respond to shouldPlay
  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    if (!shouldPlay) {
      try { el.pause(); } catch {}
    } else if (readyRef.current) {
      void playSafely();
    }
  }, [shouldPlay]);

  // pause on background
  useEffect(() => {
    const onVis = () => {
      const el = videoRef.current;
      if (!el) return;
      if (document.visibilityState === "hidden") {
        try { el.pause(); } catch {}
      } else if (shouldPlay && readyRef.current) {
        void playSafely();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [shouldPlay]);

  return { videoRef, ready, hasFrame };
}
